<template>
  <div v-if="localField.isUsedInFieldValues" class="tw-mb-5">
    <WdsAlert :title="$t('Caution')" warning>
      <template #description>
        {{ cautionText }}
      </template>
    </WdsAlert>
  </div>

  <div class="tw-mb-3">
    <FieldNameInput v-model="localName" :disabled="loading" :error="nameErrors" />
  </div>

  <div class="tw-mb-3">
    <SlugInput
      v-model="localSlug"
      :disable="loading"
      :error="slugErrors"
      :is-updated="localField.id !== null"
    />
  </div>

  <div v-if="fieldTypeComponent">
    <component
      :is="fieldTypeComponent"
      v-model:field="localField"
      :loading="loading"
      :validation-errors="validationErrors"
    />
  </div>

  <div class="tw-mb-3" data-test="custom-field-type">
    <WdsRadioGroup
      name="field_type"
      :label="$t('Input type')"
      :checked="localField.fieldType"
      :error="fieldTypeErrors"
      @update:checked="fieldTypeChanged"
    >
      <WdsRadio
        v-for="option in fieldTypeOptions"
        :key="option.value"
        :disabled="fieldTypeLocked"
        :value="option.value"
      >
        <WdsText :sub-text="option.subText" :text="option.text" />
      </WdsRadio>
    </WdsRadioGroup>
  </div>

  <WdsInputArea
    v-model="localDescription"
    data-test="custom-field-description"
    :disable="loading"
    :label="$t('Description')"
    :error="descriptionErrors"
    height="70px"
    class="tw-mb-3"
  />

  <hr />

  <div v-if="appliesTo === TypeSupportingCustomFieldValues.Job && !isFileUpload">
    <div v-if="localField.approval && !localField.settings.validation.required" class="tw-mb-5">
      <WdsAlert :title="$t('Caution')" warning>
        <template #description>
          {{ $t('It is recommended to make this field required as it is enabled for approval.') }}
        </template>
      </WdsAlert>
    </div>

    <WdsToggle
      v-model:checked="localField.approval"
      :disable="loading"
      name="approval"
      class="tw-mb-3"
      :value="localField.approval"
    >
      <div>
        <span
          data-test="enable-approval-workflows"
          v-text="$t('Enable this field for approval workflows')"
        />
        <div class="tw-text-sm tw-text-gray-500">
          {{
            $t('If you enable this field, then it will be able to be used in approval workflows.')
          }}
        </div>
      </div>
    </WdsToggle>
  </div>

  <WdsToggle
    v-model:checked="localField.settings.validation.required"
    :disable="loading"
    :error="settingsErrors"
    class="tw-mb-3"
  >
    <span data-test="custom-field-required" v-text="$t('Make this field required')" />
  </WdsToggle>

  <FeatureFlagged v-if="supportsShowOnInvoice" name="custom-fields-show-on-invoice-option">
    <WdsToggle
      :checked="showOnInvoiceDisplayValue"
      data-test="custom-field-show-on-invoice"
      :disabled="loading || !canShowOnInvoice"
      :value="showOnInvoiceDisplayValue"
      class="tw-mb-3"
      @update:checked="(value) => (localField.showOnInvoice = value)"
    >
      <div>
        <span v-text="$t('Show this field on client invoices')" />
        <div v-if="!canShowOnInvoice" class="tw-text-sm tw-text-gray-500">
          {{ $t('Only available for fields with internal visibility.') }}
        </div>
        <div v-else class="tw-text-sm tw-text-gray-500">
          {{
            $t(
              'When enabled, values for this field will be surfaced on the invoices sent to this client.',
            )
          }}
        </div>
      </div>
    </WdsToggle>
  </FeatureFlagged>

  <WdsToggle
    v-if="showSharedWithClientsToggle"
    v-model:checked="localField.sharedWithClients"
    :disable="loading"
    data-test="custom-field-shared-with-clients"
    class="tw-mb-3"
  >
    <div>
      <span v-text="$t('Share with clients')" />
      <div class="tw-text-sm tw-text-gray-500">
        {{ $t('When enabled, client companies will see this field when sharing a job with you.') }}
      </div>
    </div>
  </WdsToggle>

  <WdsToggle
    v-if="showSharedWithStaffingAgenciesToggle"
    v-model:checked="localField.sharedWithStaffingAgencies"
    :disable="loading"
    data-test="custom-field-shared-with-staffing-agencies"
    class="tw-mb-3"
  >
    <div>
      <span v-text="$t('Share with staffing agencies')" />
      <div class="tw-text-sm tw-text-gray-500">
        {{
          $t(
            'When enabled, staffing agencies you share this job with can see this field (read-only).',
          )
        }}
      </div>
    </div>
  </WdsToggle>

  <template v-if="showInputAllowedToggle">
    <WdsToggle
      v-model:checked="inputAllowedValue"
      data-test="custom-field-worker-input-allowed"
      :disabled="loading || localField.apiOnly || localField.showOnInvoice"
      :value="inputAllowedValue"
      class="tw-mb-3"
    >
      <div class="tw-max-w-xl">
        <span v-text="inputAllowedToggleLabel" />
        <div v-if="localField.apiOnly" class="tw-text-sm tw-text-red-500">
          {{ $t('Cannot be used together with API only option') }}
        </div>
        <div v-else-if="localField.showOnInvoice" class="tw-text-sm tw-text-red-500">
          {{ $t('Cannot be used together with show on invoice option') }}
        </div>
        <div v-else class="tw-text-sm tw-text-gray-500">
          {{ inputAllowedToggleDescription }}
        </div>
      </div>
    </WdsToggle>
    <div v-if="isHire && inputAllowedValue" class="tw-mb-3 tw-flex tw-items-end tw-gap-2">
      <WdsSelect
        class="tw-flex-1"
        data-test="custom-field-worker-input-stage"
        :label="$t('When should the worker complete this field?')"
        :list="workerInputStageOptions"
        :model-value="
          workerInputStageOptions.find(({ value }) => value === localField.workerInputStage)
        "
        :disabled="loading"
        @update:model-value="
          localField.workerInputStage = $event?.value ?? CustomFieldWorkerInputStage.BeforeAcceptance
        "
      />
      <WdsTooltip
        v-if="localField.workerInputStage === CustomFieldWorkerInputStage.BeforeOffer"
        :text="
          $t(
            'This also applies to existing draft hires that have not been offered yet. If the field is required, they cannot be offered until it is answered.',
          )
        "
      >
        <WdsIcon :size="16" name="info" class="tw-mb-2.5 tw-text-gray-500" />
      </WdsTooltip>
    </div>
  </template>

  <template v-if="isHire">
    <WdsToggle
      v-model:checked="localField.recruiterInputAllowed"
      data-test="custom-field-recruiter-input-allowed"
      :disabled="loading || localField.apiOnly || localField.showOnInvoice"
      class="tw-mb-3"
    >
      <div class="tw-max-w-xl">
        <span v-text="$t('Staffing agency completes this field')" />
        <div v-if="localField.apiOnly" class="tw-text-sm tw-text-red-500">
          {{ $t('Cannot be used together with API only option') }}
        </div>
        <div v-else-if="localField.showOnInvoice" class="tw-text-sm tw-text-red-500">
          {{ $t('Cannot be used together with show on invoice option') }}
        </div>
        <div v-else class="tw-text-sm tw-text-gray-500">
          {{ $t('The staffing agency answers this on hires where it manages the worker.') }}
        </div>
      </div>
    </WdsToggle>
    <div v-if="localField.recruiterInputAllowed" class="tw-mb-3 tw-flex tw-items-end tw-gap-2">
      <WdsSelect
        class="tw-flex-1"
        data-test="custom-field-recruiter-input-stage"
        :label="$t('When should the staffing agency complete this field?')"
        :list="workerInputStageOptions"
        :model-value="
          workerInputStageOptions.find(({ value }) => value === localField.recruiterInputStage)
        "
        :disabled="loading"
        @update:model-value="
          localField.recruiterInputStage =
            $event?.value ?? CustomFieldWorkerInputStage.BeforeAcceptance
        "
      />
      <WdsTooltip
        v-if="localField.recruiterInputStage === CustomFieldWorkerInputStage.BeforeOffer"
        :text="
          $t(
            'This also applies to existing draft hires that have not been offered yet. If the field is required, they cannot be offered until it is answered.',
          )
        "
      >
        <WdsIcon :size="16" name="info" class="tw-mb-2.5 tw-text-gray-500" />
      </WdsTooltip>
    </div>
  </template>

  <WdsToggle
    v-if="!isFileUpload"
    v-model:checked="localField.apiOnly"
    data-test="custom-field-api-only"
    :disabled="loading || hasInputAllowed || isExpenseField"
    :value="localField.apiOnly"
  >
    <div>
      <span v-text="$t('Make this field updateable through the api only')" />
      <!-- API-only fields are Internal, and expense fields must be visible to workers. -->
      <div v-if="isExpenseField" class="tw-text-sm tw-text-gray-500">
        {{ $t('Not available for expense fields, as they must be visible to workers.') }}
      </div>
      <div v-else-if="hasInputAllowed" class="tw-text-sm tw-text-red-500">
        {{ inputAllowedConflictText }}
      </div>
    </div>
  </WdsToggle>
</template>

<script setup lang="ts">
import { computed, toRef, watch, Ref } from 'vue'
import {
  WdsAlert,
  WdsIcon,
  WdsRadioGroup,
  WdsInputArea,
  WdsToggle,
  WdsRadio,
  WdsSelect,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { useVModel } from '@vueuse/core'
import {
  CustomFieldType,
  CustomFieldVisibility,
  CustomFieldWorkerInputStage,
  TypeSupportingCustomFieldValues,
} from '@/gql/graphql'
import { $t } from '@/composables/translation'
import FieldSingleSelect from '@/components/Fields/fieldTypeComponents/FieldSingleSelect.vue'
import { CustomField } from '@/components/Fields/dataObjects/CustomField'
import {
  getCustomFieldCautionText,
  newInstanceOfCustomField,
} from '@/components/Fields/utils/CustomFieldUtils'
import { useGraphQlError } from '@/composables/errors'
import SlugInput from '@/components/Fields/commonFields/SlugInput.vue'
import FieldNameInput from '@/components/Fields/commonFields/FieldNameInput.vue'
import FeatureFlagged from '@/components/FeatureFlagged.vue'

const emits = defineEmits(['update:field', 'fieldTypeChanged'])

export interface Props {
  field: CustomField
  validationErrors?: Object
  loading: boolean
  appliesTo: TypeSupportingCustomFieldValues
  workerOnboardingEnabled?: boolean
  isSupplier?: boolean
  isCompany?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  validationErrors: () => ({}),
})

const mapFieldTypeComponent = {
  [CustomFieldType.SingleSelect]: FieldSingleSelect,
  [CustomFieldType.MultiSelect]: FieldSingleSelect,
}

const localField: Ref<CustomField> = useVModel(props, 'field', emits)

const localName = computed<string | undefined>({
  get: () => localField.value.title ?? undefined,
  set: (newValue) => {
    localField.value.title = newValue ?? null
  },
})

const localSlug = computed<string | undefined>({
  get: () => localField.value.slug ?? undefined,
  set: (newValue) => {
    localField.value.slug = newValue ?? null
  },
})

const localDescription = computed<string | undefined>({
  get: () => localField.value.description ?? undefined,
  set: (newValue) => {
    localField.value.description = newValue ?? null
  },
})

const fieldTypeComponent = computed(() => mapFieldTypeComponent[localField.value.fieldType])

const allFieldTypeOptions = [
  {
    value: CustomFieldType.FreeText,
    text: $t('Text'),
    subText: $t('Choose this if you need to be able have any text entered as a value'),
  },
  {
    value: CustomFieldType.SingleSelect,
    text: $t('Dropdown'),
    subText: $t('Choose this if you need to have a defined list selected as a value'),
  },
  {
    value: CustomFieldType.MultiSelect,
    text: $t('Multi-select dropdown'),
    subText: $t('Choose this if you need to select multiple values from a defined list'),
  },
  {
    value: CustomFieldType.FileUpload,
    text: $t('File upload'),
    subText: $t('Choose this if you need users to upload a file for this field'),
  },
  {
    value: CustomFieldType.Date,
    text: $t('Date'),
    subText: $t('Choose this if you need a single calendar date as a value'),
  },
  {
    value: CustomFieldType.Boolean,
    text: $t('Boolean'),
    subText: $t('Choose this if you need a yes or no answer'),
  },
]

const isExpenseField = computed(() => props.appliesTo === TypeSupportingCustomFieldValues.Expense)

const isClassification = computed(
  () => props.appliesTo === TypeSupportingCustomFieldValues.ClassificationInput,
)

const fieldTypeOptions = computed(() => {
  if (isExpenseField.value) {
    return allFieldTypeOptions.filter((option) => option.value === CustomFieldType.SingleSelect)
  }

  return isClassification.value
    ? allFieldTypeOptions.filter((option) => option.value !== CustomFieldType.FileUpload)
    : allFieldTypeOptions
})

const errors = toRef(props, 'validationErrors')

const nameErrors = useGraphQlError(errors, 'title')
const descriptionErrors = useGraphQlError(errors, 'description')
const settingsErrors = useGraphQlError(errors, 'settings')
const slugErrors = useGraphQlError(errors, 'slug')
const fieldTypeErrors = useGraphQlError(errors, 'fieldType')

// Changing the type drops every option, including ones that can't be removed.
const hasBlockedOptions = computed(() =>
  localField.value.customFieldOptions.some((option) => option.isRemovalBlocked),
)

// Set on each radio: WdsRadioGroup has no prop for disabling its options.
const fieldTypeLocked = computed(
  () => props.loading || localField.value.isUsedInFieldValues || hasBlockedOptions.value,
)

function fieldTypeChanged(value: string | boolean) {
  const newType = fieldTypeOptions.value.find((option) => option.value === value)?.value
  if (newType && newType !== localField.value.fieldType) {
    const clone = { ...localField.value }
    localField.value = newInstanceOfCustomField(
      {
        id: clone.id!,
        appliesTo: clone.appliesTo,
        visibility: clone.visibility,
        title: clone.title!,
        description: clone.description!,
        approval: clone.approval!,
        isUsedInFieldValues: clone.isUsedInFieldValues,
        apiOnly: clone.apiOnly,
        workerInputAllowed: clone.workerInputAllowed,
        workerInputStage: clone.workerInputStage,
        recruiterInputAllowed: clone.recruiterInputAllowed,
        recruiterInputStage: clone.recruiterInputStage,
        showOnInvoice: clone.showOnInvoice,
        sharedWithClients: clone.sharedWithClients,
        sharedWithStaffingAgencies: clone.sharedWithStaffingAgencies,
      },
      newType,
    )

    emits('fieldTypeChanged', newType)
  }
}

const cautionText = computed(() => getCustomFieldCautionText(props.appliesTo))

const isFileUpload = computed(() => localField.value.fieldType === CustomFieldType.FileUpload)

const isCompanyRecruiter = computed(
  () => props.appliesTo === TypeSupportingCustomFieldValues.CompanyRecruiter,
)

// Client fields are internal bookkeeping; surfacing them on invoices is out of scope.
const supportsShowOnInvoice = computed(
  () =>
    props.appliesTo !== TypeSupportingCustomFieldValues.CompanySupplier && !isClassification.value,
)

const showSharedWithClientsToggle = computed(
  () => props.isSupplier && props.appliesTo === TypeSupportingCustomFieldValues.Job,
)

const showSharedWithStaffingAgenciesToggle = computed(
  () => props.isCompany && props.appliesTo === TypeSupportingCustomFieldValues.Job,
)

const isHire = computed(() => props.appliesTo === TypeSupportingCustomFieldValues.Hire)

const showInputAllowedToggle = computed(
  () =>
    isHire.value ||
    isClassification.value ||
    (props.workerOnboardingEnabled &&
      (props.appliesTo === TypeSupportingCustomFieldValues.TrustedContact ||
        props.appliesTo === TypeSupportingCustomFieldValues.CompanyRecruiter)),
)

const workerInputStageOptions = [
  {
    value: CustomFieldWorkerInputStage.BeforeAcceptance,
    label: $t('Before the contract is accepted'),
  },
  { value: CustomFieldWorkerInputStage.BeforeOffer, label: $t('Before the hire is offered') },
]

const inputAllowedValue = computed({
  get: () =>
    isCompanyRecruiter.value
      ? localField.value.recruiterInputAllowed
      : localField.value.workerInputAllowed,
  set: (value: boolean) => {
    if (isCompanyRecruiter.value) {
      localField.value.recruiterInputAllowed = value
    } else {
      localField.value.workerInputAllowed = value
    }
  },
})

const hireInputAllowed = computed(
  () => localField.value.workerInputAllowed || localField.value.recruiterInputAllowed,
)

const hasInputAllowed = computed(() =>
  isHire.value ? hireInputAllowed.value : inputAllowedValue.value,
)

const inputAllowedToggleLabel = computed(() => {
  if (isHire.value) return $t('Worker completes this field')

  if (isClassification.value) return $t('Ask the worker to fill this in')

  return isCompanyRecruiter.value
    ? $t('Use for staffing agency onboarding')
    : $t('Use for worker onboarding')
})

const inputAllowedToggleDescription = computed(() => {
  if (isHire.value) {
    return $t('The worker answers this on hires where no staffing agency manages them.')
  }

  if (isClassification.value) {
    return $t(
      'The worker answers this with their classification questions. Leave it off and your own team answers it instead.',
    )
  }

  return isCompanyRecruiter.value
    ? $t('Make this field visible and editable by the staffing agency')
    : $t('Make this field visible and editable by workers')
})

const inputAllowedConflictText = computed(() => {
  if (isHire.value) {
    return $t('Cannot be used when the worker or staffing agency completes this field')
  }

  return isCompanyRecruiter.value
    ? $t('Cannot be used together with staffing agency edit option')
    : $t('Cannot be used together with worker onboarding option')
})

const canShowOnInvoice = computed(
  () => localField.value.visibility === CustomFieldVisibility.Internal,
)

// Expense and timesheet-line values always reach the invoice. Internal payment request
// fields only do when showOnInvoice is set, so their toggle shows the real value.
const alwaysShownOnInvoice = computed(
  () =>
    isExpenseField.value ||
    (props.appliesTo === TypeSupportingCustomFieldValues.PaymentRequest && !canShowOnInvoice.value),
)

const showOnInvoiceDisplayValue = computed(() =>
  alwaysShownOnInvoice.value ? true : localField.value.showOnInvoice,
)

watch(inputAllowedValue, (allowed) => {
  if (allowed) {
    localField.value.visibility = CustomFieldVisibility.Worker
    localField.value.apiOnly = false
    if (isHire.value) {
      localField.value.workerInputStage ??= CustomFieldWorkerInputStage.BeforeAcceptance
    }
  } else if (isHire.value) {
    localField.value.workerInputStage = null
  } else if (isClassification.value) {
    localField.value.visibility = CustomFieldVisibility.Internal
  }
})

watch(
  () => localField.value.recruiterInputAllowed,
  (allowed) => {
    if (!isHire.value) return

    if (allowed) {
      localField.value.visibility = CustomFieldVisibility.Worker
      localField.value.apiOnly = false
      localField.value.recruiterInputStage ??= CustomFieldWorkerInputStage.BeforeAcceptance
    } else {
      localField.value.recruiterInputStage = null
    }
  },
)

watch(hireInputAllowed, (allowed) => {
  // Hire fields are only shown to the worker or agency when they answer them.
  if (isHire.value && !allowed) {
    localField.value.visibility = CustomFieldVisibility.Internal
  }
})

watch(canShowOnInvoice, (eligible) => {
  if (!eligible) {
    localField.value.showOnInvoice = false
  }
})

watch(
  () => localField.value.showOnInvoice,
  (showOnInvoice) => {
    if (showOnInvoice) {
      localField.value.workerInputAllowed = false
      localField.value.recruiterInputAllowed = false
    }
  },
)

watch(
  () => localField.value.apiOnly,
  (apiOnly) => {
    if (apiOnly) {
      localField.value.workerInputAllowed = false
      localField.value.recruiterInputAllowed = false
      localField.value.visibility = CustomFieldVisibility.Internal
    }
  },
)

watch(
  () => localField.value.fieldType,
  (fieldType) => {
    if (fieldType !== CustomFieldType.FileUpload) return
    if (!localField.value.apiOnly && !localField.value.approval) return
    localField.value.apiOnly = false
    localField.value.approval = false
    emits('update:field', localField.value)
  },
  { immediate: true },
)
</script>
